import React, { useEffect, useMemo, useState } from "react";
import Form from "react-bootstrap/Form";
import { Col, Row } from "react-bootstrap";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";

import { apiFetch, API } from "../../services/api";
import {
    blockKeysForSurface,
    blockLabel,
    filterCategoryAppearances,
    categoryAppearanceOption,
} from "../../helpers/experiments";

// Éditeur de leviers « en données » d'une surface.
//
// Un seul éditeur pour deux écrans : la modale d'expérience (leviers de la
// variante testée) et la page « Défauts de surface » (leviers appliqués à tout
// le monde). Les deux éditent EXACTEMENT le même objet de config — c'est la
// promesse de la phase 2 : conclure une expérience revient à recopier la config
// de la variante gagnante dans les défauts de la surface.
//
// L'état des leviers est porté par le parent (`levers` + `onChange(changes)`),
// l'éditeur ne garde que ses listes d'options (catégories, marchands).
//
// Ordre des blocs : le sélecteur multiple de react-select conserve l'ordre de
// SÉLECTION dans sa valeur — c'est donc l'ordre de rendu.

export const CTA_COLOR_HINT =
    "Formats acceptés : #rgb, #rrggbb ou #rrggbbaa, ou un nom de couleur CSS (ex : crimson). Vide = couleur actuelle.";

export const SIZES_FIRST_HINT = "Mobile uniquement : sur desktop, le sélecteur de taille ne bouge pas.";
export const BEST_OFFER_FIRST_HINT = "Nouveau design, mobile uniquement : la carte « Meilleure offre » passe sous le nom du produit, avant la galerie. Sur desktop, rien ne change.";

export const BLOCKS_ORDER_DESKTOP_HINT =
    "Sur desktop, l'ordre s'applique à l'intérieur de chaque zone (en-tête, colonne gauche, colonne droite, bas de page) ; " +
    "un bloc ne change pas de colonne.";

const toOption = (value, label) => ({ value, label: label || value });

export default function LeverEditor({ surface, levers, onChange, market = "fr", showVariantKey = true }) {
    const [categoryAppearances, setCategoryAppearances] = useState([]);
    const [merchantOptions, setMerchantOptions] = useState([]);

    // Catégories de la home (mêmes vignettes que « Catégories V2 - Recherche »)
    // et marchands : alimentent les leviers `categories` et `highlightMerchant`.
    // Les deux endpoints sont interrogés dans la langue du MARCHÉ CIBLÉ par
    // l'expérience (et non celui sélectionné dans le back-office) : c'est ce
    // marché-là qui verra la config.
    useEffect(() => {
        apiFetch(API.back + "/appearances?headCategory=/appearance/home/search-category-V2", {
            method: "GET",
            market,
        })
            .then((res) => (res.ok ? res.json() : []))
            .then((slides) => setCategoryAppearances(Array.isArray(slides) ? slides : []))
            .catch((err) => console.log(err));

        apiFetch(API.products + "/merchants", { method: "GET", market })
            .then((res) => (res.ok ? res.json() : []))
            .then((merchants) => {
                const list = Array.isArray(merchants) ? merchants : [];
                setMerchantOptions(list.filter((m) => m && m.name).map((m) => toOption(m.name)));
            })
            .catch((err) => console.log(err));
    }, [market]);

    // Le registre renvoie toutes les vignettes, tous univers et toutes langues :
    // on ne propose que l'athlétisme dans la langue du marché ciblé, sinon la
    // config nommerait des catégories que la home ne rend jamais.
    const categoryOptions = useMemo(
        () => filterCategoryAppearances(categoryAppearances, market).map(categoryAppearanceOption),
        [categoryAppearances, market]
    );

    const blockOptions = blockKeysForSurface(surface).map((key) => toOption(key, blockLabel(surface, key)));
    const orderKey = surface === "product" ? "blocksOrder" : "blocks";
    const orderedBlocks = levers[orderKey] || [];

    if (surface === "site") {
        return (
            <p className="text-muted small">
                Surface « site entier » : la variante B sert le nouveau design (v2) sur toutes les pages, le contrôle A reste le site actuel.
                Aucun levier à configurer. Les ambassadeurs voient toujours le nouveau design et ne sont pas comptés dans le test ; les robots ne sont jamais assignés.
            </p>
        );
    }
        return (
        <React.Fragment>
            <Form.Group>
                <Form.Label>Ordre des blocs</Form.Label>
                <Select
                    isMulti
                    placeholder="Choisir les blocs, dans l'ordre voulu…"
                    options={blockOptions}
                    value={orderedBlocks.map((key) => toOption(key, blockLabel(surface, key)))}
                    onChange={(selected) => onChange({ [orderKey]: (selected || []).map((o) => o.value) })}
                />
                <Form.Text className="text-muted">
                    L'ordre de sélection est l'ordre de rendu. Les blocs non choisis sont ajoutés à la suite, dans l'ordre
                    actuel du site — aucun bloc ne disparaît. Laisser vide = ordre actuel.
                </Form.Text>
                {surface === "product" ? (
                    <Form.Text className="text-muted">{BLOCKS_ORDER_DESKTOP_HINT}</Form.Text>
                ) : null}
                {orderedBlocks.length > 0 ? (
                    <div className="small mt-1">
                        {orderedBlocks.map((key, index) => (
                            <span key={key} className="mr-2">
                                {index + 1}. {blockLabel(surface, key)}
                            </span>
                        ))}
                    </div>
                ) : null}
            </Form.Group>

            {surface === "home" ? (
                <Form.Group>
                    <Form.Label>Catégories affichées (ordre de sélection)</Form.Label>
                    <CreatableSelect
                        isMulti
                        placeholder="Laisser vide pour garder toutes les catégories…"
                        options={categoryOptions}
                        formatCreateLabel={(input) => 'Ajouter l\'URL "' + input + '"'}
                        value={(levers.categories || []).map((url) => {
                            const known = categoryOptions.find((o) => o.value === url);
                            return known || toOption(url);
                        })}
                        onChange={(selected) => onChange({ categories: (selected || []).map((o) => o.value) })}
                    />
                    <Form.Text className="text-muted">
                        Liste ordonnée d'URLs de catégories à garder sur la home. Vide = toutes les catégories, dans l'ordre
                        du back-office. Seules les catégories de l'univers « athletisme » du marché{" "}
                        <b>{String(market).toUpperCase()}</b> sont proposées ; une autre URL peut être saisie à la main.
                    </Form.Text>
                </Form.Group>
            ) : null}

            {surface === "home" ? (
                <Form.Group>
                    <Form.Label>Suggestions sous la barre de recherche (une par ligne : Libellé | /lien)</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={5}
                        placeholder={"Pegasus 41 | /products/shoes?keywords=pegasus-41\nChaussures trail | /products/shoes?shopType=trail"}
                        value={levers.searchSuggestions || ""}
                        onChange={(e) => onChange({ searchSuggestions: e.target.value })}
                    />
                    <Form.Text className="text-muted">
                        Puces cliquables affichées sous la barre de recherche de la home (8 maxi, libellé de 40 caractères
                        maxi, lien commençant par / ou https://). Vide = pas de puces (comportement actuel).
                    </Form.Text>
                </Form.Group>
            ) : null}

            {surface === "product" ? (
                <React.Fragment>
                    <Row>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Libellé du bouton d'achat</Form.Label>
                                <Form.Control
                                    type="text"
                                    maxLength={60}
                                    value={levers.ctaLabel || ""}
                                    placeholder="Ex : Voir le meilleur prix"
                                    onChange={(e) => onChange({ ctaLabel: e.target.value })}
                                />
                                <Form.Text className="text-muted">
                                    Vide = libellé actuel. 60 caractères maximum (au-delà, l'API refuse la config).
                                </Form.Text>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Couleur du bouton</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={levers.ctaColor || ""}
                                    placeholder="Ex : #e63946"
                                    onChange={(e) => onChange({ ctaColor: e.target.value })}
                                />
                                <Form.Text className="text-muted">{CTA_COLOR_HINT}</Form.Text>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Form.Group>
                        <Form.Label>Marchand mis en avant</Form.Label>
                        <CreatableSelect
                            isClearable
                            placeholder="Aucun (tri par prix, comme aujourd'hui)…"
                            options={merchantOptions}
                            value={levers.highlightMerchant ? toOption(levers.highlightMerchant) : null}
                            onChange={(option) => onChange({ highlightMerchant: option ? option.value : "" })}
                        />
                        <Form.Text className="text-muted">Le marchand choisi est trié en tête et porte un badge.</Form.Text>
                    </Form.Group>
                    <Form.Group>
                        <Form.Check
                            type="checkbox"
                            id={"voucherInCta-" + surface}
                            label="Afficher le code promo dans le bouton d'achat"
                            checked={levers.voucherInCta === true}
                            onChange={(e) => onChange({ voucherInCta: e.target.checked })}
                        />
                        <Form.Check
                            type="checkbox"
                            id={"sizesFirst-" + surface}
                            label="Afficher le sélecteur de taille avant la meilleure offre"
                            checked={levers.sizesFirst === true}
                            onChange={(e) => onChange({ sizesFirst: e.target.checked })}
                        />
                        <Form.Text className="text-muted">{SIZES_FIRST_HINT}</Form.Text>
                        <Form.Text className="text-muted">
                            Décoché = valeur par défaut de la surface (la case n'écrase pas le défaut, elle l'active).
                        </Form.Text>
                    </Form.Group>
                    <Form.Group>
                        <Form.Check
                            type="checkbox"
                            id={"bestOfferFirst-" + surface}
                            label="Meilleure offre en tête, avant les photos"
                            checked={levers.bestOfferFirst === true}
                            onChange={(e) => onChange({ bestOfferFirst: e.target.checked })}
                        />
                        <Form.Text className="text-muted">{BEST_OFFER_FIRST_HINT}</Form.Text>
                    </Form.Group>
                </React.Fragment>
            ) : null}

            {showVariantKey ? (
                <Form.Group>
                    <Form.Label>Variante codée côté front (optionnel)</Form.Label>
                    <Form.Control
                        type="text"
                        value={levers.variantKey || ""}
                        placeholder="Ex : home-blocks-order — laisser vide pour un test 100 % en données"
                        onChange={(e) => onChange({ variantKey: e.target.value })}
                    />
                    <Form.Text className="text-muted">
                        À renseigner uniquement si la variante est codée dans le front. Une variante codée ne peut pas être
                        figée en données : elle reste servie à 100 % (sans cache CDN) jusqu'au déploiement du front.
                    </Form.Text>
                </Form.Group>
            ) : null}
        </React.Fragment>
    );
}
